import { TranslationWidth } from '@angular/common';
import { Component, ElementRef, forwardRef, HostBinding, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import type { ControlValueAccessor } from '@angular/forms';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import type { NgbDate, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { NgbCalendar, NgbDatepicker, NgbDatepickerI18n } from '@ng-bootstrap/ng-bootstrap';
import { default as moment, Moment } from 'moment';
import { fromEvent, Observable, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { momentToNgbDate, ngbDateToMoment } from './datepicker-utility';
import { MuiDateRange } from './mui-date-range';


@Component({
  selector: 'mui-datepicker',
  templateUrl: './mui-datepicker.component.html',
  styleUrls: [
    './mui-datepicker.component.scss',
    './ngb-datepicker.overrides.scss'
  ],
  providers: [
    { provide: NG_VALUE_ACCESSOR, multi: true, useExisting: forwardRef(() => MuiDatepicker) },
    { provide: NgbDatepickerI18n, useClass: MuiDatepicker }
  ],
  exportAs: 'muiDatepicker'
})
/**
 * @deprecated Use the new MuiDatepickerV2 and MuiDateRangePicker instead
 */
export class MuiDatepicker extends NgbDatepickerI18n implements OnInit, OnDestroy, ControlValueAccessor {
  @Input()
  closeOnSelect = false;

  @Input()
  range = false;

  @Input()
  rangeAllowSingleDate = false;

  @Input()
  displayMonths = 1;

  @Input()
  showWeekNumbers = false;

  @Input()
  minDate: Moment | null = null;

  @Input()
  maxDate: Moment | null = null;


  @HostBinding('class.hidden')
  hidden = true;

  @ViewChild(NgbDatepicker)
  datepicker!: NgbDatepicker;

  model: NgbDateStruct | null = null;
  fromDate: Moment | null = null;
  toDate: Moment | null = null;

  hasBeenReset = false;

  readonly onSelect$: Observable<Moment | MuiDateRange | null>;
  protected readonly momentToNgbDate = momentToNgbDate;

  private _onChange!: (_: Moment | MuiDateRange) => void;
  private _onTouched!: (_: any) => void;
  private _triggerElements: HTMLElement[] = [];
  private _close$ = new Subject<void>();
  private _open$ = new Subject<void>();
  private _selected = new Subject<Moment | MuiDateRange | null>();

  constructor(private elementRef: ElementRef, private calendar: NgbCalendar) {
    super();
    this.onSelect$ = this._selected.asObservable();
  }

  ngOnInit(): void {
    if (this.model && !this.range) {
      this._selected.next(ngbDateToMoment(this.model));
    }
  }

  reset(): void {
    this.model = this.calendar.getToday();
    this._selected.next(null);
    this.hasBeenReset = true;
    this.fromDate = null;
    this.toDate = null;
    this.close();
    setTimeout(() => {
      this.hasBeenReset = false;
    }, 1);
  }

  ngOnDestroy(): void {
    this._open$.complete();
    this._close$.complete();
  }

  getWeekdayLabel(weekday: number, width?: TranslationWidth): string {
    if (weekday === 7) {
      weekday = 0;
    }

    switch (width) {
      case TranslationWidth.Wide:
        return moment.weekdays()[weekday];
      case TranslationWidth.Short:
        return moment.weekdaysMin()[weekday];
      case TranslationWidth.Narrow:
        return moment.weekdaysMin()[weekday][0];
      default:
        return moment.weekdaysShort()[weekday];
    }
  }

  getDayAriaLabel(date: NgbDateStruct): string {
    const m = ngbDateToMoment(date);
    return m.format('LL');
  }

  getMonthFullName(month: number): string {
    return moment.months()[month - 1];
  }

  getMonthShortName(month: number): string {
    return moment.monthsShort()[month - 1];
  }

  /* Part of ControlValueAccessor Interface */
  writeValue(value: any): void {
    if (!this.range) {
      const date = moment(value);
      if (date.isValid()) {
        this._handleDateSelect(date);

        this.model = momentToNgbDate(date);
        if (this.datepicker) {
          this.datepicker.navigateTo(this.model);
        }
      } else {
        this.model = null;
      }
    } else {
      if (value && value.fromDate) {
        this._handleRangeSelect(value.fromDate);
      }
      if (value && value.toDate) {
        this._handleRangeSelect(value.toDate);
      }
    }
  }

  navigateTo(date: Moment): void {
    this.datepicker.navigateTo(momentToNgbDate(date));
  }

  registerOnChange(fn: (_: any) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: (_: any) => void): void {
    this._onTouched = fn;
  }

  registerTriggerElement(element: HTMLElement): void {
    this._triggerElements.push(element);
  }

  onDateSelect($event: NgbDate): void {
    const date = ngbDateToMoment($event);

    if (this.range) {
      this._handleRangeSelect(date);
    } else {
      this._handleDateSelect(date);
    }
  }

  isSelectedDate(date: NgbDate): boolean {
    if (this.range) {
      return this.isFromDate(date) || this.isToDate(date);
    } else if (this.model) {
      return date.equals(this.model);
    } else {
      return false;
    }
  }

  isRangeSelected(): Moment | false | null {
    return this.range && this.fromDate && this.toDate;
  }

  isFromDate(date: NgbDate): boolean {
    return this.range ? ngbDateToMoment(date).isSame(this.fromDate, 'day') : false;
  }

  isToDate(date: NgbDate): boolean {
    return this.range ? ngbDateToMoment(date).isSame(this.toDate, 'day') : false;
  }

  isInDateRange(date: NgbDate): boolean {
    if (this.range) {
      const mDate = ngbDateToMoment(date);
      return mDate.isBetween(this.fromDate, this.toDate, 'day');
    } else {
      return false;
    }
  }

  open(): void {
    this.hidden = false;
    this._open$.next();
    setTimeout(() => {
      this._waitForOutsideClickAndClose();
    }, 1);
  }

  close(): void {
    this.hidden = true;
    this._close$.next();
  }

  setFormValue(date: Moment | null): void {
    if (date) {
      this._onChange(date);
    }
  }

  private _handleDateSelect(date: Moment): void {
    if (this._onChange) {
      // A simple date picker: Just emit the date
      this._onChange(date);
    }

    this._selected.next(date);

    if (this.closeOnSelect) {
      this.close();
    }
  }

  private _handleRangeSelect(date: Moment): void {
    // This is a Date Range picker
    if (!this.fromDate && !this.toDate) {
      // Set fromDate first
      this.fromDate = date;
    } else if (this.fromDate && !this.toDate && date.isSameOrAfter(this.fromDate)) {
      if (this.rangeAllowSingleDate && date.isSameOrAfter(this.fromDate)) {
        this.toDate = date;
      } else if (!this.rangeAllowSingleDate && date.isAfter(this.fromDate)) {
        this.toDate = date;
      }
      // Set to Date when fromDate has already been set, toDate is undefined and the date is after the fromDate
    } else {
      this.toDate = null;
      this.fromDate = date;
    }

    // Emit the new range to outer components
    const dateRange = { fromDate: this.fromDate, toDate: this.toDate };

    if (this._onChange) {
      this._onChange(dateRange);
    }

    this._selected.next(dateRange);
  }

  private _waitForOutsideClickAndClose(): void {
    fromEvent(document, 'click').pipe(
      takeUntil(this._close$),
      filter((event: Event) => {
        const target: HTMLElement = event.target as HTMLElement;
        /* Only close datepicker when element is nor a child of the datepicker itself not the trigger element that opened the
         picker */
        return !this._elementIsContainedInHost(target) && !this._elementIsTriggerElement(target);
      })
    ).subscribe((_) => {
      this.close();
    });
  }

  private _elementIsTriggerElement(element: HTMLElement): boolean {
    return this._triggerElements.indexOf(element) >= 0;
  }

  private _elementIsContainedInHost(element: HTMLElement): boolean {
    let current = element;

    while (current.parentElement) {
      if (current === this.elementRef.nativeElement) {
        return true;
      }
      current = current.parentElement;
    }

    return false;
  }
}
